import { Fragment, memo, useCallback, useMemo } from 'react';
import { ScrollViewProps, StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { SwipeRow } from 'react-native-swipe-list-view';
import { Divider, Icon, Text, useTheme } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsEqual from 'lodash/isEqual';

import constants from '../../../Config/constants';
import MealPlanHelper from '../../../Helper/MealPlan';
import themeStyle from '../../../Theme/styles';

import Button from '../../Button';
import EmptyData from '../../EmptyData';
import MealPlanItem from '../../StoreCart/MealPlanItem';

import useMealPlanItemList from './controller/useMealPlanItemList';

export type NavState = {
  isDisplayOnly?: boolean;
  route: { value: number }; // day number (1 = Monday, 2 = Tuesday, etc..)
  scrollHandlers?: ScrollViewProps;
};

const hiddenItemWidth = 75;
const DayItemList = ({ isDisplayOnly, route, scrollHandlers }: NavState) => {
  const theme = useTheme();
  const {
    swipeRowRef,
    cart,
    cartCounter,
    note,
    getKey,
    onQtyChange,
    onRemove,
    onSwipeBegun,
  } = useMealPlanItemList(route.value);

  const mealSchedule = useMemo(
    // fixed 5_day, to get all the schedule since route.value is 1,3,5 for 3 day meal plan
    () => MealPlanHelper.generateSchedule('5_day')[route.value - 1], // -1 because value for monday is 1 but the generateSchedule start from 0
    [route.value],
  );

  // TODO - add proper item type when 'beta' branch is merged to 'main' branch
  const _renderHiddenItem = useCallback(
    (item: any) => (
      <View
        style={[themeStyle.flex1, themeStyle.alignItemsEnd]}
        key={getKey({ prefix: 'hidden', item })}
      >
        <Button
          style={[
            themeStyle.flex1,
            themeStyle.alignItemsCenter,
            themeStyle.justifyContentCenter,
            {
              width: hiddenItemWidth,
              backgroundColor: theme['color-danger-500'],
            },
          ]}
          onPress={() => onRemove(item)}
          plain
        >
          <Icon
            name="trash-o"
            pack="fa"
            fill={theme['color-basic-100']}
            style={styles.trashIcon}
          />
        </Button>
      </View>
    ),
    [theme, getKey, onRemove],
  );

  // TODO - add proper item type when 'beta' branch is merged to 'main' branch
  const _renderVisibleItem = useCallback(
    (item: any) => (
      <View
        key={getKey({ item })}
        style={[
          !constants.isWeb && themeStyle.pageBackground,
          themeStyle.pageHorizontalSpacing,
        ]}
      >
        <MealPlanItem
          {...item}
          date={mealSchedule}
          disableIncrement={
            route.value === 1
              ? cartCounter.current === constants.MEAL_PLAN_MAX_ITEM_PER_DAY
              : cartCounter.current >= cartCounter.monday
          }
          onRemove={
            constants.isWeb && !isDisplayOnly ? () => onRemove(item) : undefined
          }
          onQtyChange={
            !isDisplayOnly ? (qty: number) => onQtyChange(item, qty) : undefined
          }
        />
      </View>
    ),
    [
      cartCounter,
      isDisplayOnly,
      mealSchedule,
      route.value,
      getKey,
      onQtyChange,
      onRemove,
    ],
  );

  // TODO - add proper item type when 'beta' branch is merged to 'main' branch
  const _renderItem = useCallback(
    (item: any, index: number) => {
      if (constants.isWeb) {
        return _renderVisibleItem(item);
      } else {
        return (
          // @ts-ignore: bug on library, the SwipeRow type check doesnt accept children, but it does
          <SwipeRow
            ref={(r) => (swipeRowRef.current[getKey({ item })] = r)}
            key={getKey({ prefix: 'row', item })}
            preview={isDisplayOnly ? false : index === 0}
            previewOpenValue={-hiddenItemWidth}
            rightOpenValue={-hiddenItemWidth}
            stopRightSwipe={-hiddenItemWidth}
            swipeGestureBegan={() => onSwipeBegun(item.id)}
            disableLeftSwipe={isDisplayOnly}
            disableHiddenLayoutCalculation
            disableRightSwipe
          >
            {/* Hidden Item */}
            {_renderHiddenItem(item)}
            {/* Visible Item */}
            {_renderVisibleItem(item)}
          </SwipeRow>
        );
      }
    },
    [
      isDisplayOnly,
      swipeRowRef,
      _renderHiddenItem,
      _renderVisibleItem,
      getKey,
      onSwipeBegun,
    ],
  );

  if (lodashIsEmpty(cart)) {
    return (
      <View
        style={[
          themeStyle.flex1,
          themeStyle.pageVerticalSpacing,
          themeStyle.justifyContentCenter,
          themeStyle.alignItemsCenter,
        ]}
      >
        <EmptyData
          type="cart"
          subtitle="Added meals for this date will appear here."
          svgWidth={144}
          svgHeight={144}
        />
      </View>
    );
  }

  return (
    <Fragment>
      <ScrollView bounces={false} {...scrollHandlers}>
        {cart.map((item: any, index: number) => (
          <Fragment key={getKey({ item })}>
            {_renderItem(item, index)}
            {index !== cart.length - 1 && (
              <Divider style={themeStyle.divider} />
            )}
          </Fragment>
        ))}
      </ScrollView>
      {!isDisplayOnly && Boolean(note) && (
        <View
          style={[
            themeStyle.pageVerticalSpacingSmall,
            themeStyle.pageHorizontalSpacing,
          ]}
        >
          <Text
            appearance="hint"
            category="p2"
            status={route.value === 1 ? 'basic' : 'danger'}
            style={[
              route.value === 1 && themeStyle.italic,
              themeStyle.textCenter,
            ]}
          >
            {note}
          </Text>
        </View>
      )}
    </Fragment>
  );
};

const styles = StyleSheet.create({
  trashIcon: {
    width: 25,
    height: 25,
  },
});

export default memo(DayItemList, (prevProps, nextProps) => {
  return lodashIsEqual(prevProps.route, nextProps.route); // only rerender from parent if route value changed
});
